import React, { useEffect, useMemo, useState } from 'react';
import {
  useOrderBook,
  useOrderBookAppendBottomMutation,
  useOrderBookClearMutation,
  useOrderBookRefreshDepthMutation,
} from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import {
  buildOrderBookRefreshDepthPayload,
  getOrderBookDepthRefreshPolicy,
} from '../utils/orderBookDepthRefreshPolicy';
import { useHomebaseModal } from './HomebaseModal';
import {
  ObBuilderFairBar,
  ObBuilderProvider,
  ObBuilderSizeCell,
  useObBuilderState,
} from './ObBuilder';

/** Single hook so all OB mutations are always defined together (avoids stale/missed hook lines in bundle). */
const OB_COLLAPSED_COMP_KEY = 'riskhomeObCollapsedCompetitors';
/** Hide synthetic (competitor-only) ladder rows that have no Taken stake at that price bucket. */
const OB_HIDE_COMP_ONLY_ROWS_KEY = 'riskhomeObHideCompetitorOnlyRows';
/** Stable fallback so `useMemo` deps are not new `[]` every render when API omits arrays. */
const EMPTY_ARRAY = [];

function useOrderBookTerminalMutations(marketKey) {
  return {
    clear: useOrderBookClearMutation(marketKey),
    appendBottom: useOrderBookAppendBottomMutation(marketKey),
    refreshDepth: useOrderBookRefreshDepthMutation(marketKey),
  };
}

function fmtPrice(decimal) {
  if (decimal == null || Number.isNaN(Number(decimal))) return '—';
  const d = Number(decimal);
  return d >= 10 ? d.toFixed(2) : d.toFixed(3);
}

/**
 * Decimal odds rounded to 2 places — competitor ladder grouping.
 * Keep in sync with Python ``decimal_odds_two_dp_bucket_key`` in ``services/utils.py``.
 */
function roundDecOdds2(dec) {
  if (dec == null || Number.isNaN(Number(dec)) || Number(dec) <= 0) return null;
  return Math.round(Number(dec) * 100) / 100;
}

/** Always 2 decimal places for competitor prices (display + tooltips). */
function fmtPriceComp(decimal) {
  if (decimal == null || Number.isNaN(Number(decimal))) return '—';
  return Number(decimal).toFixed(2);
}

function fmtPct(p) {
  if (p == null || Number.isNaN(Number(p))) return '—';
  return `${(Number(p) * 100).toFixed(2)}%`;
}

function decimalFromProb(p) {
  const x = p != null ? Number(p) : NaN;
  if (!Number.isFinite(x) || x <= 0) return null;
  return 1 / Math.min(1 - 1e-9, Math.max(1e-9, x));
}

/** BID rows represent under/NO pricing; derive under decimal from displayed P(over). */
function bidDisplayDecimalFromPOver(pOver) {
  const x = pOver != null ? Number(pOver) : NaN;
  if (!Number.isFinite(x)) return null;
  return decimalFromProb(1 - x);
}

/** NO-rung implied_prob is P(NO); display bid row P(over)=1−P(NO), but decimal is under/NO. */
function bidSideFromNoLevel(lev) {
  const pNo = lev.implied_prob != null ? Number(lev.implied_prob) : null;
  if (pNo == null || !Number.isFinite(pNo)) {
    return { pOver: null, decUnder: null };
  }
  const pOver = Math.min(1 - 1e-9, Math.max(1e-9, 1 - pNo));
  return { pOver, decUnder: decimalFromProb(pNo) };
}

/** Truncate for compact column headers (sportsbook names). */
function shortBookLabel(name, max = 12) {
  if (!name || typeof name !== 'string') return '—';
  const t = name.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(1, max - 1))}…`;
}

function fmtCompSize(displaySize, fallback) {
  const v = displaySize != null ? Number(displaySize) : Number(fallback);
  if (!Number.isFinite(v)) return '—';
  return v.toFixed(0);
}

/** Internal synthetic ladder: show posted liquidity (remaining) only. */
function fmtSynthLiquidityCell(_lev, rem) {
  const r = rem != null ? Number(rem) : NaN;
  if (!Number.isFinite(r) || r <= 0) return '—';
  return r.toFixed(1);
}

/** Stake amount at which the taken bar fills the cell width (horizontal fill per stripe). */
const TAKEN_BAR_FULL_AT = 500;

function takenBarWidthPct(amount) {
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return Math.min(100, (amount / TAKEN_BAR_FULL_AT) * 100);
}

function fmtTakenStake(amount) {
  const a = amount != null ? Number(amount) : 0;
  return Number.isFinite(a) && a > 0 ? a.toFixed(0) : '—';
}

/** One column: over-side stake (``ask`` in API) or under-side stake mapped to this YES decimal (``bid``). */
function TakenStakeCell({ variant, stake }) {
  const amt = stake != null && Number.isFinite(Number(stake)) ? Math.max(0, Number(stake)) : 0;
  const showBar = amt > 0;
  const pct = takenBarWidthPct(amt);
  const isOver = variant === 'over';
  const title =
    variant === 'over'
      ? 'Over (taken): Σ full entry fees on over legs at this YES-decimal bucket — baseline line only (no alts)'
      : 'Under (taken): Σ full entry fees on under legs mapped to this YES decimal — baseline line only (no alts)';
  return (
    <td className={`num ob-taken-cell${showBar ? ' ob-taken-cell--bar' : ''} ob-taken-cell--half`} title={title}>
      {showBar ? (
        <div className="ob-taken-bar-bg ob-taken-bar-bg--single" aria-hidden>
          <div className={`ob-taken-bar-row ${isOver ? 'ob-taken-bar-row-ask' : 'ob-taken-bar-row-bid'}`}>
            {pct > 0 ? (
              <div
                className={`ob-taken-bar-fill ${isOver ? 'ob-taken-bar-ask' : 'ob-taken-bar-bid'}`}
                style={{ width: `${pct}%` }}
              />
            ) : null}
          </div>
        </div>
      ) : null}
      <span className="ob-taken-cell-front">{fmtTakenStake(stake)}</span>
    </td>
  );
}

/** Display decimal for a competitor book row (converted Underdog when present). */
function bookDisplayDecimal(cr, side = 'over') {
  const converted = side === 'under' ? cr?.decimal_under : cr?.decimal_over;
  const raw = side === 'under' ? cr?.price_under : cr?.price_over;
  const v = converted != null ? Number(converted) : raw != null ? Number(raw) : null;
  if (v == null || Number.isNaN(v)) return null;
  return v;
}

/** Real exchange depth behind a competitor quote, distinct from `display_size` (a notional
 *  constant). Absent for every book that publishes no order_book, so `null` renders as a
 *  dash and never as 0, which would read as "no liquidity". */
function fmtBookLiq(v) {
  if (v == null || !Number.isFinite(Number(v))) return null;
  const n = Number(v);
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return String(Math.round(n));
}

/** ` · liq 3.9M` suffix for a tooltip, or '' when the book publishes no depth. */
function bookLiqSuffix(cr, side) {
  const liq = fmtBookLiq(side === 'under' ? cr.size_under : cr.size_over);
  const depth = fmtBookLiq(side === 'under' ? cr.depth_under : cr.depth_over);
  if (liq == null) return '';
  return depth != null ? ` · liq ${liq} (depth ${depth})` : ` · liq ${liq}`;
}

function compBookTooltip(cr, compDefaultSz) {
  return `${cr.book} · over ${fmtPriceComp(bookDisplayDecimal(cr, 'over'))} · size ${fmtCompSize(cr.display_size, compDefaultSz)}${bookLiqSuffix(cr, 'over')}`;
}

function compBookTooltipBid(cr, compDefaultSz) {
  return `${cr.book} · under ${fmtPriceComp(bookDisplayDecimal(cr, 'under'))} · size ${fmtCompSize(cr.display_size, compDefaultSz)}${bookLiqSuffix(cr, 'under')}`;
}

/**
 * Posted-market implied P(over) for ASK (YES) ladder — same notion as Raw Prices `price_over`.
 * Prefer converted decimal (Underdog); else 1/price_over; if only under is posted, P(over)=1−1/price_under.
 * No p_fair fallback: avoids snapping locked/no-quote books to fair on the ladder.
 */
function targetPOverFromBook(cr) {
  const po = bookDisplayDecimal(cr, 'over');
  if (po != null && po > 1 && Number.isFinite(po)) return 1 / po;
  const pu = bookDisplayDecimal(cr, 'under');
  if (pu != null && pu > 1 && Number.isFinite(pu)) {
    const pUnder = 1 / pu;
    if (pUnder > 1e-9 && pUnder < 1 - 1e-9) return 1 - pUnder;
  }
  return null;
}

/**
 * Implied P(under) from posted under decimal only. BID (NO) ladder uses this — no over / p_fair fallback.
 */
function targetPUnderFromBook(cr) {
  const pu = bookDisplayDecimal(cr, 'under');
  if (pu != null && pu > 1 && Number.isFinite(pu)) return 1 / pu;
  return null;
}

/** Display P(over) from a competitor's posted under (bid side); aligns with NO-ladder row display. */
function pOverFromCompetitorUnder(cr) {
  const pUnder = targetPUnderFromBook(cr);
  if (pUnder == null || !Number.isFinite(pUnder)) return null;
  return Math.min(1 - 1e-9, Math.max(1e-9, 1 - pUnder));
}

/** Snap competitor price to an internal rung when raw P matches or rounded decimal odds (2dp) match. */
const EPS_P_SNAP = 2e-5;

function competitorMatchesRung(pRung, targetPOver) {
  if (!Number.isFinite(pRung) || targetPOver == null || !Number.isFinite(targetPOver)) return false;
  if (Math.abs(pRung - targetPOver) <= EPS_P_SNAP) return true;
  const dR = roundDecOdds2(1 / pRung);
  const dT = roundDecOdds2(targetPOver > 0 ? 1 / targetPOver : null);
  return dR != null && dT != null && dR === dT;
}

/** Same-line competitor TOB = longest (max) posted decimal per side. */
function competitorTobDecimals(competitorBooks) {
  let maxOver = null;
  let maxUnder = null;
  for (const cr of competitorBooks || []) {
    const o = bookDisplayDecimal(cr, 'over');
    const u = bookDisplayDecimal(cr, 'under');
    if (o != null && o > 1 && Number.isFinite(o)) {
      maxOver = maxOver == null ? o : Math.max(maxOver, o);
    }
    if (u != null && u > 1 && Number.isFinite(u)) {
      maxUnder = maxUnder == null ? u : Math.max(maxUnder, u);
    }
  }
  return { maxOver, maxUnder };
}

function decimalMatchesTob(dec, tobDec) {
  const a = roundDecOdds2(dec);
  const b = roundDecOdds2(tobDec);
  return a != null && b != null && a === b;
}

/**
 * Single cTOB decimal for a side: among rungs matching competitor max at 2dp,
 * pick the closest to ``tobDec`` (tie → longer). Avoids two cTOB badges when
 * 1.730 and 1.732 both round to 1.73.
 */
function uniqueCompTobDecimal(candidateDecs, tobDec) {
  if (tobDec == null || !Number.isFinite(Number(tobDec))) return null;
  const tob = Number(tobDec);
  const matched = [];
  for (const raw of candidateDecs || []) {
    const d = raw != null ? Number(raw) : NaN;
    if (!Number.isFinite(d) || d <= 1) continue;
    if (decimalMatchesTob(d, tob)) matched.push(d);
  }
  if (!matched.length) return null;
  matched.sort((a, b) => Math.abs(a - tob) - Math.abs(b - tob) || b - a);
  return matched[0];
}

function isUniqueCompTobRow(dec, uniqueTobDec) {
  if (uniqueTobDec == null || dec == null) return false;
  const a = Number(dec);
  const b = Number(uniqueTobDec);
  return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) <= 1e-6;
}

/** Compact TOB / competitor-TOB chips for the ladder # column. */
function TobLevelMarks({ ourTob, compTob, sideLabel }) {
  if (!ourTob && !compTob) return null;
  return (
    <span className="ob-tob-marks">
      {ourTob ? (
        <span
          className="ob-tob-badge ob-tob-badge--ours"
          title={`${sideLabel} top of book (index 0). Clear/− here; + pulls the opposite side’s TOB toward this side.`}
        >
          TOB
        </span>
      ) : null}
      {compTob ? (
        <span
          className="ob-tob-badge ob-tob-badge--comp"
          title={`Competitor top of book on ${sideLabel} (longest same-line decimal). Move here to match / beat competitor TOB.`}
        >
          cTOB
        </span>
      ) : null}
    </span>
  );
}

/** BID (NO) ladder: match internal P(NO) to competitor implied P(under) = 1/price_under. */
function competitorMatchesRungPNo(pNoRung, targetPUnder) {
  if (!Number.isFinite(pNoRung) || targetPUnder == null || !Number.isFinite(targetPUnder)) return false;
  if (Math.abs(pNoRung - targetPUnder) <= EPS_P_SNAP) return true;
  const dR = roundDecOdds2(1 / pNoRung);
  const dT = roundDecOdds2(1 / targetPUnder);
  return dR != null && dT != null && dR === dT;
}

/** Comma-separated book names at this ASK/OVER rung (for collapsed competitor column). */
function competitorNamesAtAskLevel(pOver, competitorBooks) {
  const names = [];
  competitorBooks.forEach((cr) => {
    const tgt = targetPOverFromBook(cr);
    if (pOver != null && Number.isFinite(pOver) && competitorMatchesRung(pOver, tgt)) {
      names.push(cr.book);
    }
  });
  return names.sort().join(', ');
}

/** Comma-separated book names at this BID/NO rung. */
function competitorNamesAtBidLevel(pNo, competitorBooks) {
  const names = [];
  competitorBooks.forEach((cr) => {
    const tgt = targetPUnderFromBook(cr);
    if (Number.isFinite(pNo) && tgt != null && competitorMatchesRungPNo(pNo, tgt)) {
      names.push(cr.book);
    }
  });
  return names.sort().join(', ');
}

function collapsedAskLevelTooltip(pOver, competitorBooks, compDefaultSz) {
  const parts = [];
  competitorBooks.forEach((cr) => {
    const tgt = targetPOverFromBook(cr);
    if (pOver != null && Number.isFinite(pOver) && competitorMatchesRung(pOver, tgt)) {
      parts.push(compBookTooltip(cr, compDefaultSz));
    }
  });
  return parts.length ? parts.join('\n') : undefined;
}

function collapsedBidLevelTooltip(pNo, competitorBooks, compDefaultSz) {
  const parts = [];
  competitorBooks.forEach((cr) => {
    const tgt = targetPUnderFromBook(cr);
    if (Number.isFinite(pNo) && tgt != null && competitorMatchesRungPNo(pNo, tgt)) {
      parts.push(compBookTooltipBid(cr, compDefaultSz));
    }
  });
  return parts.length ? parts.join('\n') : undefined;
}

/** YES-side decimal bucket key from display P(over) — aligns with ``decimal_odds_two_dp_bucket_key`` on the API. */
function yesDecimalBucketKeyFromPOver(pOver) {
  if (!Number.isFinite(pOver) || pOver <= 0) return null;
  const d = roundDecOdds2(1 / pOver);
  return d != null ? d.toFixed(2) : null;
}

function lookupTakenSide(takenByPrice, k) {
  if (!k || !takenByPrice || typeof takenByPrice !== 'object') return { ask: 0, bid: 0 };
  const row = takenByPrice[k];
  if (!row || typeof row !== 'object') return { ask: 0, bid: 0 };
  const ask = Number(row.ask);
  const bid = Number(row.bid);
  return {
    ask: Number.isFinite(ask) ? ask : 0,
    bid: Number.isFinite(bid) ? bid : 0,
  };
}

function enrichMergedWithTaken(mergedRows, takenByPrice) {
  return mergedRows.map((e) => {
    const k = yesDecimalBucketKeyFromPOver(e.pOver);
    const t = lookupTakenSide(takenByPrice, k);
    return { ...e, takenAsk: t.ask, takenBid: t.bid };
  });
}

function collectMatchedTakenBucketKeys(rows) {
  const s = new Set();
  rows.forEach((e) => {
    const k = yesDecimalBucketKeyFromPOver(e.pOver);
    if (k) s.add(k);
  });
  return s;
}

/** Insert-only prices: ASK band if P(over) > max bid P(over) (ours + comps); else BID band. */
function orphanTakenRowsForBand(takenByPrice, matchedKeys, highestBidPOverThreshold, band) {
  const out = [];
  if (!takenByPrice || typeof takenByPrice !== 'object') return out;
  for (const k of Object.keys(takenByPrice)) {
    if (matchedKeys.has(k)) continue;
    const dec = Number(k);
    if (!Number.isFinite(dec) || dec <= 1) continue;
    const pOver = 1 / dec;
    if (!Number.isFinite(pOver)) continue;
    const onAskSide = pOver > highestBidPOverThreshold;
    if (band === 'ask' && !onAskSide) continue;
    if (band === 'bid' && onAskSide) continue;
    const t = lookupTakenSide(takenByPrice, k);
    if (t.ask <= 0 && t.bid <= 0) continue;
    out.push({
      kind: 'taken_only',
      pOver,
      decYes: dec,
      takenAsk: t.ask,
      takenBid: t.bid,
      key: `hist-${band}-${k}`,
    });
  }
  return out;
}

/** Drop competitor-only synthetics; keep internal rungs with our size and/or historical taken; keep taken-only rows with stake unless ``omitHistorical``. */
function filterMergedToOurLiquidity(mergedRows, remByIndex, opts = {}) {
  const omitHist = opts.omitHistorical === true;
  // Builder mode keeps every ladder rung, including ones the operator zeroed — a pinned rung
  // has no remaining but is still a price being shown, and must stay editable (INV-OB-15).
  const keepEmptyLadderRows = opts.keepEmptyLadderRows === true;
  return mergedRows.filter((entry) => {
    if (omitHist && entry.kind === 'taken_only') return false;
    if (entry.kind === 'taken_only') {
      const b = entry.takenBid != null ? Number(entry.takenBid) : 0;
      const a = entry.takenAsk != null ? Number(entry.takenAsk) : 0;
      return (Number.isFinite(b) && b > 0) || (Number.isFinite(a) && a > 0);
    }
    if (entry.kind === 'synthetic') return false;
    if (keepEmptyLadderRows) return true;
    const i = entry.row.i;
    const rem = remByIndex[i] != null ? Number(remByIndex[i]) : 0;
    const hasRem = Number.isFinite(rem) && rem > 1e-9;
    const tb = entry.takenBid != null ? Number(entry.takenBid) : 0;
    const ta = entry.takenAsk != null ? Number(entry.takenAsk) : 0;
    const hasTaken =
      (Number.isFinite(tb) && tb > 0) || (Number.isFinite(ta) && ta > 0);
    return hasRem || hasTaken;
  });
}

/** Synthetic rows are competitor-only levels; drop those with no Taken bid/ask at the merged bucket. */
function filterOutCompetitorOnlyRows(mergedRows) {
  return mergedRows.filter((entry) => {
    if (entry.kind !== 'synthetic') return true;
    const tb = entry.takenBid != null ? Number(entry.takenBid) : 0;
    const ta = entry.takenAsk != null ? Number(entry.takenAsk) : 0;
    return (Number.isFinite(tb) && tb > 0) || (Number.isFinite(ta) && ta > 0);
  });
}

/** Display order: highest P(over) first (descending); NaN last (stable ties). */
function sortMergedLadderByPOver(merged) {
  return [...merged].sort((a, b) => {
    const fa = Number.isFinite(a.pOver) ? a.pOver : -Infinity;
    const fb = Number.isFinite(b.pOver) ? b.pOver : -Infinity;
    const c = fb - fa;
    if (c !== 0) return c;
    const rankKind = (k) => {
      if (k === 'internal') return 0;
      if (k === 'synthetic') return 1;
      if (k === 'taken_only') return 2;
      return 3;
    };
    const ra = rankKind(a.kind);
    const rb = rankKind(b.kind);
    if (ra !== rb) return ra - rb;
    if (a.kind === 'internal' && b.kind === 'internal') return a.row.i - b.row.i;
    if (a.kind === 'synthetic' && b.kind === 'synthetic') {
      const sa = a.books.map((x) => x.book).sort().join('|');
      const sb = b.books.map((x) => x.book).sort().join('|');
      return sa.localeCompare(sb);
    }
    if (a.kind === 'taken_only' && b.kind === 'taken_only') return String(a.key).localeCompare(String(b.key));
    return 0;
  });
}

/**
 * Merge internal ladder rows with synthetic rows at competitor prices.
 * ASK (YES): `targetPOverFromBook` + match on P(over).
 * BID (NO): `targetPUnderFromBook` + match on P(NO) vs P(under); books without under never contribute.
 * Final order is sorted by P(over) descending (highest first).
 */
/**
 * Group non-snapped competitors by decimal odds rounded to 2dp → one synthetic row per price level.
 * `probIsUnder`: `t` is implied P(under); display columns use P(over)=1−P(under) and under decimal (BID / NO ladder).
 */
function groupSyntheticsByRoundedDec(rawList, opts = {}) {
  const probIsUnder = opts.probIsUnder ?? false;
  const groups = new Map();
  rawList.forEach(({ t, cr }) => {
    const decRaw = t > 0 ? 1 / t : null;
    const decRounded = roundDecOdds2(decRaw);
    if (decRounded == null || decRounded <= 0) return;
    const k = decRounded.toFixed(2);
    if (!groups.has(k)) groups.set(k, { decRounded, byBook: new Map() });
    groups.get(k).byBook.set(cr.book, cr);
  });
  const out = [];
  groups.forEach(({ decRounded, byBook }) => {
    const books = [...byBook.values()];
    if (!probIsUnder) {
      const decYes = decRounded;
      const pOver = 1 / decYes;
      out.push({ kind: 'synthetic', pOver, decYes, books });
      return;
    }
    const decUnder = decRounded;
    const pUnder = 1 / decUnder;
    const pOver = Math.min(1 - 1e-9, Math.max(1e-9, 1 - pUnder));
    out.push({ kind: 'synthetic', pOver, decUnder, books });
  });
  return out;
}

function buildMergedLadderRows(internalRows, competitorBooks, getTargetP, mergeOpts = {}) {
  const getInternalP = mergeOpts.getInternalP ?? ((ir) => ir.pOver);
  const matchRung =
    mergeOpts.matchRung ??
    ((pRung, tgt) => competitorMatchesRung(pRung, tgt));
  const synthProbIsUnder = mergeOpts.syntheticProbIsUnder ?? false;

  if (!internalRows.length) {
    const raw = [];
    competitorBooks.forEach((cr) => {
      const t = getTargetP(cr);
      if (t == null || !Number.isFinite(t)) return;
      raw.push({ t, cr });
    });
    return sortMergedLadderByPOver(groupSyntheticsByRoundedDec(raw, { probIsUnder: synthProbIsUnder }));
  }

  const internalPs = internalRows.map((ir) => {
    const v = getInternalP(ir);
    return v != null && Number.isFinite(v) ? v : NaN;
  });

  const rawSynth = [];

  competitorBooks.forEach((cr) => {
    const t = getTargetP(cr);
    if (t == null || !Number.isFinite(t)) return;
    let snapped = false;
    for (let j = 0; j < internalPs.length; j++) {
      if (matchRung(internalPs[j], t)) {
        snapped = true;
        break;
      }
    }
    if (snapped) return;
    rawSynth.push({ t, cr });
  });

  const synthetics = groupSyntheticsByRoundedDec(rawSynth, { probIsUnder: synthProbIsUnder });

  const out = [];
  internalRows.forEach((ir) => {
    out.push({ kind: 'internal', ...ir });
  });
  synthetics.forEach((s) => out.push(s));
  return sortMergedLadderByPOver(out);
}

/**
 * ASK = sell over (customer takes over) → internal YES ladder · OVER equivalent.
 * BID = buy over (customer language) → internal NO ladder · BUY OVER equivalent.
 * Mid row: our fair + demarginated p_fair per book. Rows sorted by P(over) descending.
 */
export default function OrderBookTerminal({ marketKey, quote, takenLine }) {
  const { user } = useAuth();
  const [ourLiquidityOnly, setOurLiquidityOnly] = useState(false);
  const [manualPersistAck, setManualPersistAck] = useState(false);
  const [collapsedCompetitorView, setCollapsedCompetitorView] = useState(() => {
    try {
      return sessionStorage.getItem(OB_COLLAPSED_COMP_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [hideCompetitorOnlyRows, setHideCompetitorOnlyRows] = useState(() => {
    try {
      return sessionStorage.getItem(OB_HIDE_COMP_ONLY_ROWS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const { confirm, ModalLayer } = useHomebaseModal();

  const obQuoteLine =
    takenLine != null && Number.isFinite(Number(takenLine))
      ? Number(takenLine)
      : quote != null && quote.line != null && Number.isFinite(Number(quote.line))
        ? Number(quote.line)
        : null;
  const { data: ob, isLoading, error } = useOrderBook(marketKey, {
    enabled: !!marketKey,
    quoteLine: obQuoteLine,
  });
  const obMut = useOrderBookTerminalMutations(marketKey);

  const [showBuilder, setShowBuilder] = useState(false);
  const depthPol = useMemo(() => getOrderBookDepthRefreshPolicy(quote), [quote]);
  const {
    refreshBlockedEntirely,
    refreshNeedsSoftFlag,
    liquidityDepthGate,
    liquidityShapeRefreshExempt,
    quoteEngineBlocksDepthReseed,
    isProductEffectivelyTwoSided,
    needsAckTwoSided,
    needsAckOneSided,
    manualPersistWorkflowApplies,
  } = depthPol;
  /** Tail row + per-level +/- / clear (not depth refresh buttons). */
  const obOperatorAdjDisabled =
    !marketKey || quote == null || !quote.gate_allowed || refreshBlockedEntirely;
  const obAdjGateTitle = !marketKey
    ? 'Select a market'
    : refreshBlockedEntirely
      ? 'Operator ladder controls blocked for this market'
      : quote == null
        ? 'No quote loaded — tail / +/- disabled'
        : !quote.gate_allowed
          ? 'Market gated — tail / +/- disabled'
          : '';
  const gaEff = quote != null && Boolean(quote.gate_allowed);
  const q2Eff = quote != null && Boolean(quote.quote_two_sided);

  const baseDepthDisabled =
    !marketKey || !ob?.enabled || obMut.refreshDepth.isPending || refreshBlockedEntirely;
  /** Per-button gate: hard blocks, or operator acknowledgement when refresh would touch Tier‑2 / DB shaping. */
  const twoSidedDisabled =
    baseDepthDisabled || quoteEngineBlocksDepthReseed || (needsAckTwoSided && !manualPersistAck);
  const oneSidedDisabled =
    baseDepthDisabled || quoteEngineBlocksDepthReseed || (needsAckOneSided && !manualPersistAck);

  useEffect(() => {
    setManualPersistAck(false);
  }, [marketKey]);

  useEffect(() => {
    try {
      sessionStorage.setItem(OB_COLLAPSED_COMP_KEY, collapsedCompetitorView ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsedCompetitorView]);

  useEffect(() => {
    try {
      sessionStorage.setItem(OB_HIDE_COMP_ONLY_ROWS_KEY, hideCompetitorOnlyRows ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [hideCompetitorOnlyRows]);

  const actedByOpt = useMemo(() => user?.email?.trim() || undefined, [user?.email]);

  const enabled = ob?.enabled;
  const snap = ob?.snapshot;
  const builderActive = showBuilder && Boolean(enabled) && Boolean(snap);
  const builder = useObBuilderState({
    marketKey,
    orderBook: ob,
    quote,
    actedBy: actedByOpt,
    active: builderActive,
  });
  /**
   * In builder mode the ladder *is* the preview: prices, rung counts and sizes all come from
   * the previewed snapshot so the operator edits the book they can see. Falls back to live
   * state before the first preview lands, and whenever a proposal is unbuildable.
   */
  const previewLadder = builderActive && builder.preview?.buildable ? builder.preview : null;
  const remY = useMemo(
    () =>
      previewLadder
        ? previewLadder.over.levels.map((l) => Number(l.size) || 0)
        : ob?.remaining_yes ?? EMPTY_ARRAY,
    [previewLadder, ob?.remaining_yes],
  );
  const remN = useMemo(
    () =>
      previewLadder
        ? previewLadder.under.levels.map((l) => Number(l.size) || 0)
        : ob?.remaining_no ?? EMPTY_ARRAY,
    [previewLadder, ob?.remaining_no],
  );
  const yesLevels = useMemo(
    () => (previewLadder ? previewLadder.over.levels : snap?.yes_levels ?? EMPTY_ARRAY),
    [previewLadder, snap?.yes_levels],
  );
  const noLevels = useMemo(
    () => (previewLadder ? previewLadder.under.levels : snap?.no_levels ?? EMPTY_ARRAY),
    [previewLadder, snap?.no_levels],
  );
  const competitorBooks = useMemo(
    () => ob?.competitor_books ?? EMPTY_ARRAY,
    [ob?.competitor_books],
  );
  const compDefaultSz = ob?.competitor_display_size_default ?? 100;
  const nComp = competitorBooks.length;
  const { maxOver: compTobOver, maxUnder: compTobUnder } = useMemo(
    () => competitorTobDecimals(competitorBooks),
    [competitorBooks],
  );
  /** OB operator policy comes from dashboard quote only (ladder depth ≠ publish shape; OB GET no longer ships operator_add). */
  const t1Hard = quote?.gate_breakdown?.tier1_hard;
  const obHardBlocked = Boolean(t1Hard?.blocks_synthetic_liquidity);
  const noSynthOk = gaEff && q2Eff && !obHardBlocked;
  const noTopRem = remN[0] != null ? Number(remN[0]) : 0;
  const yesTopRem = remY[0] != null ? Number(remY[0]) : 0;

  const askRows = useMemo(() => {
    return yesLevels.map((lev, i) => {
      const idx = Number.isFinite(Number(lev?.index)) ? Number(lev.index) : i;
      return { lev, idx, i, key: `ask-${idx}-${lev.decimal ?? i}` };
    });
  }, [yesLevels]);

  const bidRows = useMemo(() => {
    return noLevels
      .map((lev, i) => {
        const idx = Number.isFinite(Number(lev?.index)) ? Number(lev.index) : i;
        return { lev, idx, i, key: `bid-${idx}-${lev.decimal ?? i}` };
      })
      .reverse();
  }, [noLevels]);

  const internalAskRows = useMemo(() => {
    return bidRows.map((row) => {
      const { pOver, decUnder } = bidSideFromNoLevel(row.lev);
      const pNoRaw = row.lev?.implied_prob != null ? Number(row.lev.implied_prob) : NaN;
      return {
        row,
        pOver: pOver != null && Number.isFinite(pOver) ? pOver : NaN,
        pNo: Number.isFinite(pNoRaw) ? pNoRaw : NaN,
        decUnder,
      };
    });
  }, [bidRows]);

  const mergedAskRows = useMemo(() => {
    return buildMergedLadderRows(internalAskRows, competitorBooks, targetPUnderFromBook, {
      getInternalP: (ir) => ir.pNo,
      matchRung: (pNoRung, tgt) => competitorMatchesRungPNo(pNoRung, tgt),
      syntheticProbIsUnder: true,
    });
  }, [internalAskRows, competitorBooks]);

  const internalBidRows = useMemo(() => {
    return askRows.map((row) => {
      const lev = row.lev;
      const pRaw = lev?.implied_prob != null ? Number(lev.implied_prob) : NaN;
      const pOver = Number.isFinite(pRaw) ? pRaw : NaN;
      const decYes = Number.isFinite(pOver) && pOver > 0 ? 1 / pOver : null;
      return { row, pOver, decYes };
    });
  }, [askRows]);

  const mergedBidRows = useMemo(() => {
    return buildMergedLadderRows(internalBidRows, competitorBooks, targetPOverFromBook);
  }, [internalBidRows, competitorBooks]);

  /** Max bid P(over): our NO ladder + every competitor with a posted under (bid quote). */
  const highestBidPOverOverall = useMemo(() => {
    let m = -Infinity;
    internalAskRows.forEach((ir) => {
      if (Number.isFinite(ir.pOver)) m = Math.max(m, ir.pOver);
    });
    competitorBooks.forEach((cr) => {
      const pOver = pOverFromCompetitorUnder(cr);
      if (pOver != null && Number.isFinite(pOver)) m = Math.max(m, pOver);
    });
    return m;
  }, [internalAskRows, competitorBooks]);

  const { mergedBidWithTaken, mergedAskWithTaken } = useMemo(() => {
    const tp = ob?.taken_by_price ?? {};
    const askBandEnriched = enrichMergedWithTaken(mergedBidRows, tp);
    const bidBandEnriched = enrichMergedWithTaken(mergedAskRows, tp);
    const matched = collectMatchedTakenBucketKeys([...askBandEnriched, ...bidBandEnriched]);
    const hi = highestBidPOverOverall;
    const askOrphans = orphanTakenRowsForBand(tp, matched, hi, 'ask');
    const bidOrphans = orphanTakenRowsForBand(tp, matched, hi, 'bid');
    return {
      mergedBidWithTaken: sortMergedLadderByPOver([...askBandEnriched, ...askOrphans]),
      mergedAskWithTaken: sortMergedLadderByPOver([...bidBandEnriched, ...bidOrphans]),
    };
  }, [mergedBidRows, mergedAskRows, ob?.taken_by_price, highestBidPOverOverall]);

  /** One cTOB badge per side (ASK = over decimal, BID = under decimal). */
  const askCompTobUnique = useMemo(() => {
    const decs = [];
    for (const entry of mergedBidWithTaken || []) {
      if (entry?.kind === 'synthetic' && entry.decYes != null) decs.push(entry.decYes);
      else if (entry?.decYes != null) decs.push(entry.decYes);
      else if (entry?.row?.lev?.decimal != null) decs.push(entry.row.lev.decimal);
      else if (entry?.pOver != null && Number(entry.pOver) > 0) decs.push(1 / Number(entry.pOver));
    }
    return uniqueCompTobDecimal(decs, compTobOver);
  }, [mergedBidWithTaken, compTobOver]);

  const bidCompTobUnique = useMemo(() => {
    const decs = [];
    for (const entry of mergedAskWithTaken || []) {
      if (entry?.kind === 'synthetic' && entry.decUnder != null) decs.push(entry.decUnder);
      else if (entry?.decUnder != null) decs.push(entry.decUnder);
    }
    return uniqueCompTobDecimal(decs, compTobUnder);
  }, [mergedAskWithTaken, compTobUnder]);

  /** Human-readable summary of a stored builder shape, for the "operator-shaped" chip. */
  const shapedSummary = useMemo(() => {
    const o = ob?.builder_overrides;
    if (!o) return null;
    const bits = [];
    if (o.margin_pp_over != null) bits.push(`over margin ${o.margin_pp_over}`);
    if (o.margin_pp_under != null) bits.push(`under margin ${o.margin_pp_under}`);
    if (o.level_count_over != null) bits.push(`over ${o.level_count_over} rungs`);
    if (o.level_count_under != null) bits.push(`under ${o.level_count_under} rungs`);
    if (o.liquidity_over) bits.push(`over liquidity on ${o.liquidity_over.length} rungs`);
    if (o.liquidity_under) bits.push(`under liquidity on ${o.liquidity_under.length} rungs`);
    return bits.length ? `Operator-shaped — ${bits.join(', ')}` : 'Operator-shaped';
  }, [ob?.builder_overrides]);

  // Builder collapses to our own ladder: competitor rows and historical fills are noise while
  // shaping, and the rung indices must line up with the editable cells.
  const effectiveOurLiquidityOnly = builderActive || ourLiquidityOnly;
  const displayAskRows = useMemo(() => {
    let rows = mergedAskWithTaken;
    if (effectiveOurLiquidityOnly)
      rows = filterMergedToOurLiquidity(rows, remN, {
        omitHistorical: true,
        keepEmptyLadderRows: builderActive,
      });
    if (hideCompetitorOnlyRows) rows = filterOutCompetitorOnlyRows(rows);
    return rows;
  }, [effectiveOurLiquidityOnly, builderActive, hideCompetitorOnlyRows, mergedAskWithTaken, remN]);

  const displayBidRows = useMemo(() => {
    let rows = mergedBidWithTaken;
    if (effectiveOurLiquidityOnly)
      rows = filterMergedToOurLiquidity(rows, remY, {
        omitHistorical: true,
        keepEmptyLadderRows: builderActive,
      });
    if (hideCompetitorOnlyRows) rows = filterOutCompetitorOnlyRows(rows);
    return rows;
  }, [effectiveOurLiquidityOnly, builderActive, hideCompetitorOnlyRows, mergedBidWithTaken, remY]);

  const midP = snap?.midpoint_prob ?? quote?.p_fair;
  const obBusy =
    obMut.clear.isPending || obMut.appendBottom.isPending || obMut.refreshDepth.isPending;
  const compColCount = nComp === 0 ? 0 : collapsedCompetitorView ? 1 : nComp;
  const totalCols = 7 + compColCount;

  const depthBlockedTitle =
    'Depth refresh blocked (tier-1 hard gate, lineup hold, or manual suspend).';
  const quoteBlockedDepthTitle =
    'Quote is not publishable (e.g. innovation / posterior). Wait for the engine gate or use Manual / Tier‑2 override when the breakdown allows it.';
  const persistAckTitle =
    'Liquidity loss closes the book by itself — Depth refresh reopens without Manual unless Tier‑2 / quote-shape persist is required (then check this).';
  const depthTwoSidedCrossShapeTitle =
    'Product is one-sided (margination shape). Check “Allow Manual persistence when required” to re-seed depth as two-sided.';
  const depthOneSidedCrossShapeTitle =
    'Product is two-sided. Check “Allow Manual persistence when required” to re-seed depth as one-sided.';

  return (
    <ObBuilderProvider value={builder}>
    <div className={`ob-terminal-wrap${builderActive ? ' is-building' : ''}`}>
      <div className="ob-terminal-toolbar">
        <label
          className="ob-refresh-override"
          title={persistAckTitle}
        >
          <input
            type="checkbox"
            checked={manualPersistAck}
            onChange={(e) => setManualPersistAck(e.target.checked)}
            disabled={
              !marketKey ||
              refreshBlockedEntirely ||
              (!manualPersistWorkflowApplies && !manualPersistAck)
            }
          />
          <span>Allow Manual persistence when required</span>
        </label>
        <button
          type="button"
          className="btn-ob-refresh"
          title={
            refreshBlockedEntirely
              ? depthBlockedTitle
              : quoteEngineBlocksDepthReseed
                ? quoteBlockedDepthTitle
                : !isProductEffectivelyTwoSided &&
                    !liquidityShapeRefreshExempt &&
                    !manualPersistAck
                  ? depthTwoSidedCrossShapeTitle
                  : needsAckTwoSided && !manualPersistAck
                    ? persistAckTitle
                    : liquidityDepthGate && !refreshNeedsSoftFlag
                      ? 'Liquidity gate: full P→OB re-seed without Manual (unless Tier‑2 requires the checkbox).'
                      : 'P→OB re-seed aligned to publishable quote.'
          }
          onClick={() => {
            void (async () => {
              const ok = await confirm({
                title: 'Depth (two-sided)',
                body:
                  'Full P→OB re-seed. Liquidity-only reopen needs no checkbox. Manual DB / Tier‑2 waive only if you checked the box when required.',
                confirmLabel: 'Re-seed',
              });
              if (!ok) return;
              obMut.refreshDepth.mutate(
                buildOrderBookRefreshDepthPayload('two_sided', depthPol, manualPersistAck, actedByOpt),
                { onSuccess: () => setManualPersistAck(false) },
              );
            })();
          }}
          disabled={twoSidedDisabled}
        >
          {obMut.refreshDepth.isPending ? 'Re-seeding…' : 'Depth (two-sided)'}
        </button>
        <button
          type="button"
          className="btn-ob-refresh"
          title={
            refreshBlockedEntirely
              ? depthBlockedTitle
              : quoteEngineBlocksDepthReseed
                ? quoteBlockedDepthTitle
                : isProductEffectivelyTwoSided &&
                    !liquidityShapeRefreshExempt &&
                    !manualPersistAck
                  ? depthOneSidedCrossShapeTitle
                  : needsAckOneSided && !manualPersistAck
                    ? persistAckTitle
                    : liquidityDepthGate && !refreshNeedsSoftFlag
                      ? 'Liquidity gate: one-sided P→OB re-seed without Manual when applicable.'
                      : 'P→OB re-seed to one-sided product shape.'
          }
          onClick={() => {
            void (async () => {
              const ok = await confirm({
                title: 'Depth (one-sided)',
                body:
                  'Full P→OB re-seed. Liquidity-only reopen needs no checkbox; Manual / Tier‑2 only when required.',
                confirmLabel: 'Re-seed',
              });
              if (!ok) return;
              obMut.refreshDepth.mutate(
                buildOrderBookRefreshDepthPayload('one_sided', depthPol, manualPersistAck, actedByOpt),
                { onSuccess: () => setManualPersistAck(false) },
              );
            })();
          }}
          disabled={oneSidedDisabled}
        >
          Depth (one-sided)
        </button>
        {user?.email ? (
          <span className="ob-operator-email-chip" title="Stored as acted_by on OB actions (clear / refresh / etc.)">
            {user.email}
          </span>
        ) : null}
        {enabled && snap && (
          <button
            type="button"
            className={`btn-ob-refresh ${effectiveOurLiquidityOnly ? 'btn-ob-toggle-on' : ''}`}
            disabled={builderActive}
            title={
              ourLiquidityOnly
                ? 'Filtered: only rungs with posted size — hides depleted rows. Click for full ladder.'
                : 'Full ladder: shows competitor-only prices and depleted rungs'
            }
            aria-pressed={effectiveOurLiquidityOnly}
            onClick={() => setOurLiquidityOnly((v) => !v)}
          >
            {effectiveOurLiquidityOnly ? 'Full depth' : 'Our liquidity only'}
          </button>
        )}
        {enabled && snap && (
          <button
            type="button"
            className={`btn-ob-builder${showBuilder ? ' is-on' : ''}${
              ob?.builder_active ? ' has-shape' : ''
            }`}
            title="Shape the ladder — type liquidity straight into the size column, set rungs and margin per side, preview before committing"
            aria-pressed={showBuilder}
            onClick={() => setShowBuilder((v) => !v)}
          >
            <span className="btn-ob-builder-dot" aria-hidden="true" />
            {showBuilder ? 'Exit builder' : 'Build ladder'}
            {ob?.builder_active ? <span className="btn-ob-builder-tag">shaped</span> : null}
          </button>
        )}
        {enabled && snap && nComp > 0 && (
          <button
            type="button"
            className={`btn-ob-refresh ${hideCompetitorOnlyRows ? 'btn-ob-toggle-on' : ''}`}
            title={
              hideCompetitorOnlyRows
                ? 'Showing rows with our size, Taken, or competitor size at merged levels. Click to list competitor-only empty Taken rows again.'
                : 'Hide rows that only show competitor prices (no internal size and no Taken at that Dec bucket)'
            }
            aria-pressed={hideCompetitorOnlyRows}
            onClick={() => setHideCompetitorOnlyRows((v) => !v)}
          >
            {hideCompetitorOnlyRows ? 'Show comp-only rows' : 'Hide comp-only rows'}
          </button>
        )}

        {(snap?.effective_tick_size != null || (enabled && snap && nComp > 0)) && (
          <span className="ob-meta-chip-row">
            {snap?.effective_tick_size != null && (
              <span className="ob-meta-chip">
                Tick (logit) <strong>{Number(snap.effective_tick_size).toFixed(4)}</strong>
              </span>
            )}
            {enabled && snap && nComp > 0 && (
              <label
                className="ob-meta-chip ob-collapsed-comp-toggle"
                title="One column: comma-separated book names at each level (hover for odds/size). Uncheck for per-book columns."
              >
                <input
                  type="checkbox"
                  checked={collapsedCompetitorView}
                  onChange={(e) => setCollapsedCompetitorView(e.target.checked)}
                  aria-label="Collapsed competitor view"
                />
                <span className="ob-collapsed-comp-label">Collapsed competitor view</span>
              </label>
            )}
          </span>
        )}
        {snap?.spread_scale != null && (
          <span className="ob-meta-chip">
            Spread scale <strong>{Number(snap.spread_scale).toFixed(3)}</strong>
          </span>
        )}
      </div>

      {!marketKey && (
        <div className="empty-state compact">Select a market to load the order book.</div>
      )}

      {marketKey && isLoading && !ob && (
        <div className="empty-state compact">Loading order book…</div>
      )}

      {marketKey && error && (
        <div className="ob-error">Could not load order book: {error.message || String(error)}</div>
      )}

      {marketKey && ob && !enabled && (
        <div className="ob-disabled-note">
          Internal order book is disabled in service config (<code>entries.order_book_enabled</code>). Enable it to see ladders.
        </div>
      )}

      {enabled && !snap && !isLoading && (
        <div className="empty-state compact">
          No order book for this market yet. The service builds it from the base KF when state exists (DB restore,
          live engine, or first accepted update). If this market has no <code>kf_state</code> row or no in-memory
          engine, there is nothing to display.
        </div>
      )}

      {enabled && snap && (
        <div className="ob-terminal-layout">
          <div className="ob-spread-stack ob-spread-stack-unified">
            <div className="ob-section-title ob-section-title-unified">
              <span className="ob-section-tag ob-tag-spread">BOOK</span>
              <span className="ob-section-hint">
                Mid strip = demarginated fair P(over) per book
                {' · '}
                <span className="ob-tob-badge ob-tob-badge--ours">TOB</span>
                {' = our top (idx 0); '}
                <span className="ob-tob-badge ob-tob-badge--comp">cTOB</span>
                {' = competitor longest decimal'}
                {compTobOver != null ? ` · ask cTOB ${fmtPriceComp(compTobOver)}` : ''}
                {compTobUnder != null ? ` · bid cTOB ${fmtPriceComp(compTobUnder)}` : ''}
                {effectiveOurLiquidityOnly ? ' · showing rungs with our liquidity only' : ''}
                {previewLadder ? (
                  <span className="ob-builder-preview-flag" title="Prices, rungs and sizes below are a proposal — nothing is committed until Apply">
                    preview
                  </span>
                ) : null}
                {!previewLadder && ob?.builder_active ? (
                  <span
                    className="ob-shaped-flag"
                    title={shapedSummary || 'Operator-shaped order book'}
                  >
                    operator-shaped
                  </span>
                ) : null}
                {hideCompetitorOnlyRows ? ' · hiding competitor-only rows (no size / no Taken)' : ''}
                {obQuoteLine != null && Number.isFinite(Number(obQuoteLine))
                  ? ` · Taken columns: entries on baseline line ${Number(obQuoteLine).toFixed(2)} only (no alts)`
                  : ''}
              </span>
            </div>
            <div className="ob-table-scroll">
              <table className="ob-table ob-table-compact ob-table-unified">
                <thead>
                  <tr>
                    <th>#</th>
                    <th className="num">P(over)</th>
                    <th className="num">Dec (YES)</th>
                    <th className="num">Size</th>
                    <th className="ob-col-adj ob-col-adj-head" scope="col">
                      Adj
                    </th>
                    <th
                      className="num ob-taken-col-head ob-taken-col-head--half"
                      scope="col"
                      title="Over (taken): Σ full entry fees at this YES-decimal bucket for the baseline line only"
                    >
                      Over
                    </th>
                    <th
                      className="num ob-taken-col-head ob-taken-col-head--half"
                      scope="col"
                      title="Under (taken): Σ full entry fees mapped to this YES decimal for the baseline line only"
                    >
                      Under
                    </th>
                    {collapsedCompetitorView ? (
                        <th
                          className="ob-book-col-head ob-book-col-collapsed-head"
                          scope="col"
                          title="Books with a line at this ladder level (names only)"
                        >
                          Books @ level
                        </th>
                      ) : (
                        competitorBooks.map((r) => (
                          <th
                            key={`h-${r.book}`}
                            className="num ob-book-col-head"
                            title={`${r.book} · O ${fmtPriceComp(bookDisplayDecimal(r, 'over'))} / U ${fmtPriceComp(bookDisplayDecimal(r, 'under'))}${r.line != null ? ` · line ${r.line}` : ''}${bookLiqSuffix(r, 'over')}${bookLiqSuffix(r, 'under').replace(' · liq', ' / ')}`}
                            scope="col"
                          >
                            {shortBookLabel(r.book)}
                          </th>
                        ))
                      )}
                  </tr>
                </thead>
                <tbody>
                  {/* ASK = customer takes over → internal YES ladder */}
                  <tr className="ob-band-banner ob-band-ask">
                    <td colSpan={totalCols}>
                      <span className="ob-section-tag ob-tag-ask">
                        ASK <span className="ob-band-equiv">(OVER Price equivalent)</span>
                      </span>
                      <span className="ob-band-hint">
                        Sell over · YES · P(over) high → low
                      </span>
                    </td>
                  </tr>
                  <tr className="ob-tail-row ob-tail-row-top">
                    <td colSpan={4} className="ob-tail-label">
                      Tail (YES){' '}
                      <span className="ob-tail-meta">(deepest rung · synthetic depth only · does not move EntriesKF / opposite quote)</span>
                    </td>
                    <td className="ob-col-adj">
                      <button
                        type="button"
                        className="btn-ob-adj btn-ob-tail"
                        disabled={
                          !enabled ||
                          obBusy ||
                          yesLevels.length === 0 ||
                          obHardBlocked ||
                          obOperatorAdjDisabled
                        }
                        title={
                          obOperatorAdjDisabled
                            ? obAdjGateTitle
                            : obHardBlocked
                              ? 'Tier-1 hard gate blocks OB operator controls'
                              : 'Append deeper YES rung — internal OB only'
                        }
                        aria-label="Tail extend YES ladder"
                        onClick={() => {
                          void (async () => {
                            const ok = await confirm({
                              title: 'Extend YES tail',
                              body: 'Append one deeper YES tail rung? (internal OB only, no EntriesKF.)',
                              confirmLabel: 'Append',
                            });
                            if (!ok) return;
                            obMut.appendBottom.mutate({ side: 'yes', acted_by: actedByOpt });
                          })();
                        }}
                      >
                        tail
                      </button>
                    </td>
                    <td className="num ob-taken-cell" aria-hidden>
                      —
                    </td>
                    <td className="num ob-taken-cell" aria-hidden>
                      —
                    </td>
                    {collapsedCompetitorView ? (
                        <td className="ob-book-pad" aria-hidden />
                      ) : (
                        competitorBooks.map((r) => <td key={`tail-yes-pad-${r.book}`} className="ob-book-pad" aria-hidden />)
                      )}
                  </tr>
                  {displayBidRows.map((entry) => {
                    if (entry.kind === 'taken_only') {
                      const { pOver, decYes, takenAsk, takenBid, key } = entry;
                      return (
                        <tr key={key} className="ob-row ob-row-ask-half ob-row-synthetic">
                          <td title="Historical fills at this price (ladder may have moved)">hist</td>
                          <td className="num">{fmtPct(pOver)}</td>
                          <td className="num">{decYes != null ? fmtPriceComp(decYes) : '—'}</td>
                          <td className="num">—</td>
                          <td className="ob-col-adj">
                            <span className="ob-adj-btns">
                              <span className="ob-adj-slot" aria-hidden />
                              <span className="ob-adj-slot" aria-hidden />
                            </span>
                          </td>
                          <TakenStakeCell variant="over" stake={takenAsk} />
                          <TakenStakeCell variant="under" stake={takenBid} />
                          {collapsedCompetitorView ? (
                              <td className="ob-book-cell ob-book-cell-collapsed">—</td>
                            ) : (
                              competitorBooks.map((cr) => (
                                <td key={`${key}-${cr.book}`} className="num ob-book-cell">
                                  —
                                </td>
                              ))
                            )}
                        </tr>
                      );
                    }
                    if (entry.kind === 'synthetic') {
                      const { pOver, decYes, books } = entry;
                      const synthKey = `ask-over-synth-${decYes}-${books.map((b) => b.book).sort().join('|')}`;
                      const namesStr = books.map((b) => b.book).sort().join(', ');
                      const tipSynth = books.map((b) => compBookTooltip(b, compDefaultSz)).join('\n');
                      const isCompTob = isUniqueCompTobRow(decYes, askCompTobUnique);
                      return (
                        <tr key={synthKey} className={`ob-row ob-row-ask-half ob-row-synthetic${isCompTob ? ' ob-row-ctob' : ''}`}>
                          <td title="Competitor-only price level">
                            <span className="ob-level-idx">·</span>
                            <TobLevelMarks ourTob={false} compTob={isCompTob} sideLabel="ASK / over" />
                          </td>
                          <td className="num">{fmtPct(pOver)}</td>
                          <td className="num">{decYes != null ? fmtPriceComp(decYes) : '—'}</td>
                          <td className="num">—</td>
                          <td className="ob-col-adj">
                            <span className="ob-adj-btns">
                              <span className="ob-adj-slot" aria-hidden />
                              <span className="ob-adj-slot" aria-hidden />
                            </span>
                          </td>
                          <TakenStakeCell variant="over" stake={entry.takenAsk} />
                          <TakenStakeCell variant="under" stake={entry.takenBid} />
                          {collapsedCompetitorView ? (
                              <td
                                className={`ob-book-cell ob-book-cell-collapsed ${namesStr ? 'ob-book-cell--hot' : ''}`}
                                title={tipSynth || undefined}
                              >
                                {namesStr || '—'}
                              </td>
                            ) : (
                              competitorBooks.map((bookCr) => {
                                const cr = books.find((b) => b.book === bookCr.book);
                                const show = cr != null;
                                return (
                                  <td
                                    key={`${synthKey}-${bookCr.book}`}
                                    className={`num ob-book-cell ${show ? 'ob-book-cell--hot' : ''}`}
                                    title={show ? compBookTooltip(cr, compDefaultSz) : undefined}
                                  >
                                    {show ? fmtCompSize(cr.display_size, compDefaultSz) : '—'}
                                  </td>
                                );
                              })
                            )}
                        </tr>
                      );
                    }
                    const { row, pOver } = entry;
                    const { lev, idx, i, key } = row;
                    const rem = remY[i] != null ? Number(remY[i]) : 0;
                    const hasSize = rem > 0;
                    /** Operator API always clears index 0 on YES — merged sort order can differ from ladder index. */
                    const topOfBook = i === 0;
                    const decYes =
                      Number.isFinite(pOver) && pOver > 0 ? (1 / pOver) : Number(lev?.decimal);
                    const isCompTob = isUniqueCompTobRow(decYes, askCompTobUnique);
                    // Per-rung +/- act on the live ladder; while the builder is previewing,
                    // the rows on screen are a proposal, so an index click would land on the
                    // wrong rung. Commit or exit the builder first.
                    const ctrlsEnabled = enabled && !obBusy && !builderActive;
                    const collapsedAskNames = competitorNamesAtAskLevel(pOver, competitorBooks);
                    return (
                      <tr
                        key={`ask-over-${key}`}
                        className={`ob-row ob-row-ask-half ${hasSize ? 'ob-row-active' : 'ob-row-empty'}${topOfBook ? ' ob-row-tob' : ''}${isCompTob ? ' ob-row-ctob' : ''}`}
                      >
                        <td>
                          <span className="ob-level-idx">{idx}</span>
                          <TobLevelMarks ourTob={topOfBook} compTob={isCompTob} sideLabel="ASK / over" />
                        </td>
                        <td className="num">{fmtPct(pOver)}</td>
                        <td className="num">{fmtPrice(
                          Number.isFinite(pOver) && pOver > 0 ? (1 / pOver) : lev.decimal
                        )}</td>
                        <td className="num ob-size-cell">
                          <ObBuilderSizeCell
                            side="over"
                            index={idx}
                            current={rem}
                            level={lev}
                            fallback={fmtSynthLiquidityCell(lev, rem)}
                          />
                        </td>
                        <td className="ob-col-adj">
                          <span className="ob-adj-btns">
                            {topOfBook ? (
                              <>
                                <button
                                  type="button"
                                  className="btn-ob-adj btn-ob-minus"
                                  disabled={!ctrlsEnabled || obHardBlocked || obOperatorAdjDisabled}
                                  title={
                                    obOperatorAdjDisabled
                                      ? obAdjGateTitle
                                      : obHardBlocked
                                        ? 'Tier-1 hard gate blocks YES-side OB controls'
                                        : 'Level 0 only: strip YES top (OB_to_P). Empty top → noop / OB alignment only.'
                                  }
                                  aria-label="Pull liquidity from YES top"
                                  onClick={() => {
                                    void (async () => {
                                      const ok = await confirm({
                                        title: 'Clear YES top',
                                        body:
                                          'Clear liquidity at YES top (index 0)? Applies OB→P→EntriesKF when stake > 0.',
                                        danger: true,
                                        confirmLabel: 'Clear',
                                      });
                                      if (!ok) return;
                                      obMut.clear.mutate({
                                        side: 'yes',
                                        index: 0,
                                        pull_from_opposite_top: false,
                                        acted_by: actedByOpt,
                                      });
                                    })();
                                  }}
                                >
                                  −
                                </button>
                                <button
                                  type="button"
                                  className="btn-ob-adj btn-ob-plus"
                                  disabled={!ctrlsEnabled || obHardBlocked || obOperatorAdjDisabled}
                                  title={
                                    obOperatorAdjDisabled
                                      ? obAdjGateTitle
                                      : obHardBlocked
                                        ? 'Tier-1 hard gate blocks YES-side OB controls'
                                        : noTopRem > 1e-9
                                          ? 'Level 0: strip NO top then add to YES top'
                                          : 'One-sided: virtual NO-top clear → OB_to_P + rebuild YES ladder'
                                  }
                                  aria-label="Pull from NO top toward YES"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void (async () => {
                                      const ok = await confirm({
                                        title: 'Operator + (YES)',
                                        body:
                                          'Operator +: strip NO top and refill YES top, or one-sided virtual NO-top clear + rebuild?',
                                        confirmLabel: 'Apply',
                                      });
                                      if (!ok) return;
                                      obMut.clear.mutate({
                                        side: 'no',
                                        index: 0,
                                        pull_from_opposite_top: true,
                                        acted_by: actedByOpt,
                                      });
                                    })();
                                  }}
                                >
                                  +
                                </button>
                              </>
                            ) : (
                              <>
                                <span className="ob-adj-slot" aria-hidden />
                                <span className="ob-adj-slot" aria-hidden />
                              </>
                            )}
                          </span>
                        </td>
                        <TakenStakeCell variant="over" stake={entry.takenAsk} />
                        <TakenStakeCell variant="under" stake={entry.takenBid} />
                        {collapsedCompetitorView ? (
                            <td
                              className={`ob-book-cell ob-book-cell-collapsed ${collapsedAskNames ? 'ob-book-cell--hot' : ''}`}
                              title={collapsedAskLevelTooltip(pOver, competitorBooks, compDefaultSz)}
                            >
                              {collapsedAskNames || '—'}
                            </td>
                          ) : (
                            competitorBooks.map((cr) => {
                              const tgt = targetPOverFromBook(cr);
                              const close =
                                pOver != null && Number.isFinite(pOver) && competitorMatchesRung(pOver, tgt);
                              return (
                                <td
                                  key={`ask-over-${key}-${cr.book}`}
                                  className={`num ob-book-cell ${close ? 'ob-book-cell--hot' : ''}`}
                                  title={close ? compBookTooltip(cr, compDefaultSz) : undefined}
                                >
                                  {close ? fmtCompSize(cr.display_size, compDefaultSz) : '—'}
                                </td>
                              );
                            })
                          )}
                      </tr>
                    );
                  })}

                  <tr className="ob-book-fair-mid-row">
                    <td colSpan={7} className="ob-our-fair-mid-cell">
                      <div className="ob-mid-inline-label">Our fair · P(over)</div>
                      <div className="ob-mid-inline-val">{fmtPct(midP)}</div>
                      <div className="ob-mid-inline-sub">
                        {quote?.quote_source || '—'} · σ P {quote?.P != null ? Number(quote.P).toFixed(4) : '—'}
                      </div>
                      <ObBuilderFairBar />
                    </td>
                    {collapsedCompetitorView ? (
                        <td
                          className="ob-book-pfair-mid-cell ob-book-cell-collapsed"
                          title={competitorBooks.map((cr) => `${cr.book}: ${cr.p_fair != null && Number.isFinite(Number(cr.p_fair)) ? fmtPct(Number(cr.p_fair)) : '—'}`).join('\n')}
                        >
                          {competitorBooks
                            .map((cr) =>
                              `${shortBookLabel(cr.book)} ${cr.p_fair != null && Number.isFinite(Number(cr.p_fair)) ? fmtPct(Number(cr.p_fair)) : '—'}`
                            )
                            .join(' · ')}
                        </td>
                      ) : (
                        competitorBooks.map((cr) => (
                          <td
                            key={`mid-pfair-${cr.book}`}
                            className="num ob-book-pfair-mid-cell"
                            title={`${cr.book} · demarginated fair P(over) from posted prices`}
                          >
                            {cr.p_fair != null && Number.isFinite(Number(cr.p_fair)) ? fmtPct(Number(cr.p_fair)) : '—'}
                          </td>
                        ))
                      )}
                  </tr>

                  {/* BID = buy over (product label) → internal NO ladder */}
                  <tr className="ob-band-banner ob-band-bid">
                    <td colSpan={totalCols}>
                      <span className="ob-section-tag ob-tag-bid">
                        BID <span className="ob-band-equiv">(UNDER Price equivalent)</span>
                      </span>
                      <span className="ob-band-hint">
                        Buy over · NO · P(over) high → low
                      </span>
                    </td>
                  </tr>
                  {displayAskRows.map((entry) => {
                    if (entry.kind === 'taken_only') {
                      const { pOver, takenAsk, takenBid, key } = entry;
                      const decUnder = bidDisplayDecimalFromPOver(pOver);
                      return (
                        <tr key={key} className="ob-row ob-row-bid-half ob-row-synthetic">
                          <td title="Historical fills at this price (ladder may have moved)">hist</td>
                          <td className="num">{fmtPct(pOver)}</td>
                          <td className="num">{decUnder != null ? fmtPriceComp(decUnder) : '—'}</td>
                          <td className="num">—</td>
                          <td className="ob-col-adj">
                            <span className="ob-adj-btns">
                              <span className="ob-adj-slot" aria-hidden />
                              <span className="ob-adj-slot" aria-hidden />
                            </span>
                          </td>
                          <TakenStakeCell variant="over" stake={takenAsk} />
                          <TakenStakeCell variant="under" stake={takenBid} />
                          {collapsedCompetitorView ? (
                              <td className="ob-book-cell ob-book-cell-collapsed">—</td>
                            ) : (
                              competitorBooks.map((cr) => (
                                <td key={`${key}-${cr.book}`} className="num ob-book-cell">
                                  —
                                </td>
                              ))
                            )}
                        </tr>
                      );
                    }
                    if (entry.kind === 'synthetic') {
                      const { pOver, decUnder, books } = entry;
                      const synthKey = `bid-under-synth-${decUnder}-${books.map((b) => b.book).sort().join('|')}`;
                      const namesStrBid = books.map((b) => b.book).sort().join(', ');
                      const tipSynthBid = books.map((b) => compBookTooltipBid(b, compDefaultSz)).join('\n');
                      const isCompTob = isUniqueCompTobRow(decUnder, bidCompTobUnique);
                      return (
                        <tr key={synthKey} className={`ob-row ob-row-bid-half ob-row-synthetic${isCompTob ? ' ob-row-ctob' : ''}`}>
                          <td title="Competitor-only price level">
                            <span className="ob-level-idx">·</span>
                            <TobLevelMarks ourTob={false} compTob={isCompTob} sideLabel="BID / under" />
                          </td>
                          <td className="num">{fmtPct(pOver)}</td>
                          <td className="num">{decUnder != null ? fmtPriceComp(decUnder) : '—'}</td>
                          <td className="num">—</td>
                          <td className="ob-col-adj">
                            <span className="ob-adj-btns">
                              <span className="ob-adj-slot" aria-hidden />
                              <span className="ob-adj-slot" aria-hidden />
                            </span>
                          </td>
                          <TakenStakeCell variant="over" stake={entry.takenAsk} />
                          <TakenStakeCell variant="under" stake={entry.takenBid} />
                          {collapsedCompetitorView ? (
                              <td
                                className={`ob-book-cell ob-book-cell-collapsed ${namesStrBid ? 'ob-book-cell--hot' : ''}`}
                                title={tipSynthBid || undefined}
                              >
                                {namesStrBid || '—'}
                              </td>
                            ) : (
                              competitorBooks.map((bookCr) => {
                                const cr = books.find((b) => b.book === bookCr.book);
                                const show = cr != null;
                                return (
                                  <td
                                    key={`${synthKey}-${bookCr.book}`}
                                    className={`num ob-book-cell ${show ? 'ob-book-cell--hot' : ''}`}
                                    title={show ? compBookTooltipBid(cr, compDefaultSz) : undefined}
                                  >
                                    {show ? fmtCompSize(cr.display_size, compDefaultSz) : '—'}
                                  </td>
                                );
                              })
                            )}
                        </tr>
                      );
                    }
                    const { row, pOver, decUnder } = entry;
                    const { lev, idx, i, key } = row;
                    const pNo = lev?.implied_prob != null ? Number(lev.implied_prob) : NaN;
                    const rem = remN[i] != null ? Number(remN[i]) : 0;
                    const hasSize = rem > 0;
                    /** Operator API clears NO top at index 0 — use ladder index, not merged-table row order. */
                    const topOfBook = i === 0;
                    const isCompTob = isUniqueCompTobRow(decUnder, bidCompTobUnique);
                    // Per-rung +/- act on the live ladder; while the builder is previewing,
                    // the rows on screen are a proposal, so an index click would land on the
                    // wrong rung. Commit or exit the builder first.
                    const ctrlsEnabled = enabled && !obBusy && !builderActive;
                    const collapsedBidNames = competitorNamesAtBidLevel(pNo, competitorBooks);
                    return (
                      <tr
                        key={`bid-under-${key}`}
                        className={`ob-row ob-row-bid-half ${hasSize ? 'ob-row-active' : 'ob-row-empty'}${topOfBook ? ' ob-row-tob' : ''}${isCompTob ? ' ob-row-ctob' : ''}`}
                      >
                        <td>
                          <span className="ob-level-idx">{idx}</span>
                          <TobLevelMarks ourTob={topOfBook} compTob={isCompTob} sideLabel="BID / under" />
                        </td>
                        <td className="num">{fmtPct(pOver)}</td>
                        <td className="num">{fmtPrice(decUnder)}</td>
                        <td className="num ob-size-cell">
                          <ObBuilderSizeCell
                            side="under"
                            index={idx}
                            current={rem}
                            level={lev}
                            fallback={fmtSynthLiquidityCell(lev, rem)}
                          />
                        </td>
                        <td className="ob-col-adj">
                          <span className="ob-adj-btns">
                            {topOfBook ? (
                              <>
                                <button
                                  type="button"
                                  className="btn-ob-adj btn-ob-minus"
                                  disabled={!ctrlsEnabled || !noSynthOk || obOperatorAdjDisabled}
                                  title={
                                    obOperatorAdjDisabled
                                      ? obAdjGateTitle
                                      : !noSynthOk
                                        ? obHardBlocked
                                          ? 'Tier-1 hard gate blocks NO-side OB controls'
                                          : !q2Eff
                                            ? 'One-sided — NO OB controls off'
                                            : 'Quote not publishing — NO OB controls off'
                                        : 'Level 0 only: strip NO top (OB_to_P). Empty top → noop / OB alignment only.'
                                  }
                                  aria-label="Pull liquidity from NO top"
                                  onClick={() => {
                                    void (async () => {
                                      const ok = await confirm({
                                        title: 'Clear NO top',
                                        body:
                                          'Clear liquidity at NO top (index 0)? Applies OB→P→EntriesKF when stake > 0.',
                                        danger: true,
                                        confirmLabel: 'Clear',
                                      });
                                      if (!ok) return;
                                      obMut.clear.mutate({
                                        side: 'no',
                                        index: 0,
                                        pull_from_opposite_top: false,
                                        acted_by: actedByOpt,
                                      });
                                    })();
                                  }}
                                >
                                  −
                                </button>
                                <button
                                  type="button"
                                  className="btn-ob-adj btn-ob-plus"
                                  disabled={!ctrlsEnabled || !noSynthOk || obOperatorAdjDisabled}
                                  title={
                                    obOperatorAdjDisabled
                                      ? obAdjGateTitle
                                      : !noSynthOk
                                        ? obHardBlocked
                                          ? 'Tier-1 hard gate blocks NO-side OB controls'
                                          : !q2Eff
                                            ? 'One-sided — no NO +'
                                            : 'Quote not publishing — NO + off'
                                        : yesTopRem > 1e-9
                                          ? 'Level 0: strip YES top then add to NO top'
                                          : 'One-sided: virtual YES-top clear → OB_to_P + rebuild NO ladder'
                                  }
                                  aria-label="Pull from YES top toward NO"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void (async () => {
                                      const ok = await confirm({
                                        title: 'Operator + (NO)',
                                        body:
                                          'Operator +: strip YES top and refill NO top, or one-sided virtual YES-top clear + rebuild?',
                                        confirmLabel: 'Apply',
                                      });
                                      if (!ok) return;
                                      obMut.clear.mutate({
                                        side: 'yes',
                                        index: 0,
                                        pull_from_opposite_top: true,
                                        acted_by: actedByOpt,
                                      });
                                    })();
                                  }}
                                >
                                  +
                                </button>
                              </>
                            ) : (
                              <>
                                <span className="ob-adj-slot" aria-hidden />
                                <span className="ob-adj-slot" aria-hidden />
                              </>
                            )}
                          </span>
                        </td>
                        <TakenStakeCell variant="over" stake={entry.takenAsk} />
                        <TakenStakeCell variant="under" stake={entry.takenBid} />
                        {collapsedCompetitorView ? (
                            <td
                              className={`ob-book-cell ob-book-cell-collapsed ${collapsedBidNames ? 'ob-book-cell--hot' : ''}`}
                              title={collapsedBidLevelTooltip(pNo, competitorBooks, compDefaultSz)}
                            >
                              {collapsedBidNames || '—'}
                            </td>
                          ) : (
                            competitorBooks.map((cr) => {
                              const tgt = targetPUnderFromBook(cr);
                              const close =
                                Number.isFinite(pNo) && tgt != null && competitorMatchesRungPNo(pNo, tgt);
                              return (
                                <td
                                  key={`bid-under-${key}-${cr.book}`}
                                  className={`num ob-book-cell ${close ? 'ob-book-cell--hot' : ''}`}
                                  title={close ? compBookTooltipBid(cr, compDefaultSz) : undefined}
                                >
                                  {close ? fmtCompSize(cr.display_size, compDefaultSz) : '—'}
                                </td>
                              );
                            })
                          )}
                      </tr>
                    );
                  })}
                  <tr className="ob-tail-row ob-tail-row-bottom">
                    <td colSpan={4} className="ob-tail-label">
                      Tail (NO){' '}
                      <span className="ob-tail-meta">(deepest rung · synthetic depth only · does not move EntriesKF / opposite quote)</span>
                    </td>
                    <td className="ob-col-adj">
                      <button
                        type="button"
                        className="btn-ob-adj btn-ob-tail"
                        disabled={
                          !enabled ||
                          obBusy ||
                          noLevels.length === 0 ||
                          !noSynthOk ||
                          obOperatorAdjDisabled
                        }
                        title={
                          obOperatorAdjDisabled
                            ? obAdjGateTitle
                            : noSynthOk
                              ? 'Append deeper NO rung — internal OB only'
                              : obHardBlocked
                                ? 'Tier-1 hard gate blocks OB operator controls'
                                : !q2Eff
                                  ? 'One-sided — NO synthetic ladder closed'
                                  : 'Quote not publishing — NO adds disabled'
                        }
                        aria-label="Tail extend NO ladder"
                        onClick={() => {
                          void (async () => {
                            const ok = await confirm({
                              title: 'Extend NO tail',
                              body: 'Append one deeper NO tail rung? (internal OB only, no EntriesKF.)',
                              confirmLabel: 'Append',
                            });
                            if (!ok) return;
                            obMut.appendBottom.mutate({ side: 'no', acted_by: actedByOpt });
                          })();
                        }}
                      >
                        tail
                      </button>
                    </td>
                    <td className="num ob-taken-cell" aria-hidden>
                      —
                    </td>
                    <td className="num ob-taken-cell" aria-hidden>
                      —
                    </td>
                    {collapsedCompetitorView ? (
                        <td className="ob-book-pad" aria-hidden />
                      ) : (
                        competitorBooks.map((r) => <td key={`tail-no-pad-${r.book}`} className="ob-book-pad" aria-hidden />)
                      )}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {obMut.clear.isError && (
        <div className="ob-error">
          Clear failed: {obMut.clear.error?.response?.data?.detail || obMut.clear.error?.message || 'Error'}
        </div>
      )}
      {obMut.appendBottom.isError && (
        <div className="ob-error">
          Append tail failed:{' '}
          {obMut.appendBottom.error?.response?.data?.detail ||
            obMut.appendBottom.error?.message ||
            'Error'}
        </div>
      )}
      {obMut.refreshDepth.isError && (
        <div className="ob-error">
          Depth refresh failed:{' '}
          {obMut.refreshDepth.error?.response?.data?.detail ||
            obMut.refreshDepth.error?.message ||
            'Error'}
        </div>
      )}
      <ModalLayer />
    </div>
    </ObBuilderProvider>
  );
}
